// "In other versions" section of a clause (ADR-0035): the same clause in every linked version of the grid code, newest first, each with what changed since the version before it (the earliest marked as such) and a link to it, this version marked; plus the version that added the clause and the first later version without it. Hidden when no other version holds or lacks it.
import type { ClauseChange, ClauseHistory } from "@/api/__generated__";
import { ClauseLink } from "@/components/common/ClauseLink";
import { Badge } from "@/components/ui/badge";

export const CLAUSE_CHANGE_LABELS: Record<ClauseChange, string> = {
  unchanged: "Unchanged",
  text_changed: "Text changed",
  context_changed: "Tables, figures or definitions changed",
  renumbered: "Renumbered",
};

export function hasHistory(history: ClauseHistory): boolean {
  return history.versions.length > 0 || history.added_in !== null || history.dropped_in !== null;
}

export function ClauseVersions({ history }: { history: ClauseHistory }) {
  const newestFirst = [...history.versions].reverse();
  return (
    <div className="flex flex-col gap-2">
      {newestFirst.length > 0 ? (
        <ol
          className="m-0 flex list-none flex-col gap-1.5 p-0"
          aria-label="Versions of this clause, newest first"
        >
          {newestFirst.map((version, index) => {
            const before = newestFirst[index + 1];
            return (
              <li
                key={version.clause_uid}
                className="flex flex-wrap items-center gap-x-2.5 gap-y-1 rounded-md border bg-card px-3 py-2"
                aria-current={version.current ? "true" : undefined}
              >
                <span className="text-ui font-semibold text-foreground">{version.version_label}</span>
                {version.current ? (
                  <span className="font-mono text-xs text-foreground">{version.number}</span>
                ) : (
                  <ClauseLink clauseUid={version.clause_uid} number={version.number} />
                )}
                {version.current ? <Badge variant="outline">This version</Badge> : null}
                <span className="ml-auto text-chip text-muted-foreground">
                  {version.change === null || before === undefined
                    ? "Earliest linked version"
                    : `${CLAUSE_CHANGE_LABELS[version.change]} since ${before.version_label}`}
                </span>
              </li>
            );
          })}
        </ol>
      ) : null}
      {history.added_in === null ? null : (
        <p className="m-0 text-chip text-ink-2">
          New in {history.added_in}: the version before it has no such clause.
        </p>
      )}
      {history.dropped_in === null ? null : (
        <p className="m-0 text-chip text-ink-2">
          Not in {history.dropped_in}: removed there, or replaced by a clause with other wording.
        </p>
      )}
    </div>
  );
}
